<template>
  <router-view />
</template>

<script setup>
</script>

<style lang="scss">
#app {
  width: 100%;
  height: 100%;

  .el-backtop {
    background: $white;
    border: 1.5px solid $cream-300;
    border-radius: 50%;
    box-shadow: $shadow-sm;
    transition: all $dur-fast $ease-out;

    .el-icon {
      color: $orange;
    }

    &:hover {
      background: $orange;
      border-color: $orange;
      transform: translateY(-3px);
      box-shadow: $shadow-glow;

      .el-icon {
        color: $white;
      }
    }
  }
}

// ---------------------------------------------------------
// Dialog overlay
// ---------------------------------------------------------
.el-overlay:has(.messageBox),
.el-overlay-dialog:has(.carrom-dialog),
.el-overlay:has(.carrom-dialog) {
  background-color: rgba(42, 30, 20, 0.4) !important;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

// ---------------------------------------------------------
// ElMessageBox — wood-themed card
// ---------------------------------------------------------
.el-message-box.messageBox {
  background: $white !important;
  border: 1.5px solid $cream-300 !important;
  border-radius: $r-lg !important;
  padding: 28px !important;
  width: 400px !important;
  overflow: hidden;
  box-shadow: $shadow-lg;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: $grad-orange;
    border-radius: $r-lg $r-lg 0 0;
  }

  .el-message-box__header {
    padding: 0 0 14px !important;

    .el-message-box__title {
      color: $text-strong !important;
      font-size: 18px;
      font-weight: 700;
      font-family: $font-display;
    }

    .el-message-box__headerbtn {
      width: 30px;
      height: 30px;
      background: rgba(245, 146, 10, 0.06);
      border: 1px solid $cream-300;
      border-radius: 50%;
      transition: all $dur-fast $ease-out;
      top: 0;
      right: 0;

      .el-message-box__close {
        color: $text-muted;
      }

      &:hover {
        background: $orange;
        border-color: transparent;

        .el-message-box__close {
          color: $white;
        }
      }
    }
  }

  .el-message-box__content {
    padding: 0 0 22px !important;

    .el-message-box__container {
      .el-message-box__status {
        color: $orange !important;
      }

      .el-message-box__message p {
        color: $text-muted !important;
        line-height: 1.7;
        font-family: $font-body;
        font-size: 14px;
      }
    }
  }

  .el-message-box__btns {
    padding: 0 !important;
    display: flex;
    justify-content: flex-end;
    gap: 10px;

    .el-button--primary {
      background: $grad-orange !important;
      border: none !important;
      border-radius: $r-pill !important;
      font-size: 13px;
      font-weight: 600;
      color: $white !important;
      padding: 9px 24px !important;
      height: auto !important;
      line-height: 1.5;
      box-shadow: $shadow-glow;
      transition: all $dur-fast $ease-out;
      font-family: $font-alt;

      &:hover {
        box-shadow: $shadow-glow-amber;
        transform: translateY(-1px);
      }
    }

    .el-button:not(.el-button--primary) {
      background: $white !important;
      border: 1.5px solid $cream-300 !important;
      border-radius: $r-pill !important;
      color: $text-muted !important;
      font-size: 13px;
      font-weight: 500;
      padding: 9px 24px !important;
      height: auto !important;
      line-height: 1.5;
      transition: all $dur-fast $ease-out;
      font-family: $font-alt;

      &:hover {
        border-color: $orange !important;
        color: $orange !important;
      }
    }
  }
}

// ---------------------------------------------------------
// Shared dialog shell (login / pay)
// ---------------------------------------------------------
.el-dialog.carrom-dialog {
  background: $white !important;
  border: 1.5px solid $cream-300 !important;
  border-radius: $r-xl !important;
  padding: 0 !important;
  overflow: hidden;
  box-shadow: $shadow-lg;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: $grad-orange;
    z-index: 2;
  }

  .el-dialog__header {
    margin: 0;
    padding: 26px 30px 0;
  }

  .el-dialog__title {
    font-family: $font-display;
    font-size: 22px;
    font-weight: 700;
    color: $text-strong;
  }

  .el-dialog__headerbtn {
    top: 20px;
    right: 22px;
    width: 32px;
    height: 32px;
    background: rgba(245, 146, 10, 0.06);
    border: 1px solid $cream-300;
    border-radius: 50%;
    transition: all $dur-fast $ease-out;

    .el-dialog__close {
      color: $text-muted;
    }

    &:hover {
      background: $orange;
      border-color: transparent;

      .el-dialog__close {
        color: $white;
      }
    }
  }

  .el-dialog__body {
    padding: 22px 30px 30px;
    color: $text-body;
  }

  // Form controls
  .el-input__wrapper,
  .el-select__wrapper,
  .el-textarea__inner {
    background: $cream-50 !important;
    box-shadow: none !important;
    border: 1.5px solid $cream-300 !important;
    border-radius: $r-md !important;
    padding: 6px 14px;
    transition: border-color $dur-fast $ease-out,
      box-shadow $dur-fast $ease-out;

    &:hover {
      border-color: $wood-300 !important;
    }

    &.is-focus,
    &:focus-within {
      border-color: $orange !important;
      box-shadow: 0 0 0 3px rgba(245, 146, 10, 0.1) !important;
    }
  }

  .el-input__inner,
  .el-textarea__inner {
    color: $text-strong !important;
    font-family: $font-body;

    &::placeholder {
      color: $text-light !important;
    }
  }

  .el-form-item__label {
    color: $text-body !important;
    font-family: $font-alt;
    font-size: 13px;
    font-weight: 500;
  }

  .el-form-item__error {
    font-size: 12px;
    color: $danger !important;
  }
}
</style>
